/**
 * Admin Publisher Management Console
 * 
 * GOVERNANCE ANALYTICS SURFACE
 * 
 * PURPOSE: Admin-only, read-only governance analytics for Publishers,
 * enabling drill-down from Publisher → Managed Authors → Manuscripts.
 * 
 * DATA SOURCES:
 * - reader_credit_ledger = RC Consumed (economic truth)
 * - sessions = Playback Sessions
 * - audia_session_summaries = Ask Audia Sessions
 * 
 * DESIGN PRINCIPLES:
 * 1. Admin-Only, Read-Only (Pre-MVP)
 * 2. Engagement ≠ Earnings - explicit labeling
 * 3. Reader-Only Economic Activity
 * 4. Date-bounded queries (default: 30 days)
 * 
 * DRILL-DOWN HIERARCHY:
 * Publisher → Managed Authors → Manuscripts → Engagement Summary
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import AdminNav from '../components/AdminNav';
import AdminTopMenuBar from '../components/AdminTopMenuBar';
import AdminMobileBottomNav from '../components/AdminMobileBottomNav';
import UniversalFooter from '../components/UniversalFooter';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Building2,
  Users,
  BookOpen,
  Clock,
  CreditCard,
  MessageSquare,
  TrendingUp,
  Search,
  RefreshCw,
  Eye,
  AlertCircle,
  Loader2,
  ChevronRight,
  Calendar,
  BarChart3,
  ArrowLeft,
  Trash2
} from 'lucide-react';
import { useNotification } from '@/contexts/NotificationContext';
import { API_URL } from '@/config/api';

// API_URL imported from centralized config

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toLocaleString() || '0';
};

const formatRC = (rc) => {
  return `${rc?.toFixed(2) || '0.00'} RC`;
};

const formatSeconds = (seconds) => {
  if (!seconds || seconds === 0) return '0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'active': return 'bg-green-500/20 text-green-300 border-green-500/50';
    case 'draft': return 'bg-amber-500/20 text-amber-300 border-amber-500/50';
    case 'inactive': return 'bg-red-500/20 text-red-300 border-red-500/50';
    default: return 'bg-slate-500/20 text-slate-300 border-slate-500/50';
  }
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// Non-Payout Disclaimer Banner
const EngagementDisclaimer = () => (
  <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-3 mb-6">
    <div className="flex items-start gap-2">
      <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs text-amber-300 font-medium">
          Engagement / Consumption Metrics (Non-Payout)
        </p>
        <p className="text-xs text-amber-400/80 mt-0.5">
          These figures represent Reader activity and content usage signals. They do not represent earnings, revenue share, or payout obligations.
        </p>
      </div>
    </div>
  </div>
);

// Breadcrumb Navigation
const Breadcrumb = ({ items }) => (
  <nav className="flex items-center gap-2 text-sm mb-4">
    {items.map((item, index) => (
      <React.Fragment key={index}>
        {index > 0 && <ChevronRight className="w-4 h-4 text-slate-500" />}
        {item.onClick ? (
          <button
            onClick={item.onClick}
            className="text-blue-400 hover:text-blue-300 transition-colors"
          >
            {item.label}
          </button>
        ) : (
          <span className="text-slate-300">{item.label}</span>
        )}
      </React.Fragment>
    ))}
  </nav>
);

// Metric Card
const MetricCard = ({ icon: Icon, label, value, subValue, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-500/20 text-blue-400",
    green: "bg-green-500/20 text-green-400",
    purple: "bg-purple-500/20 text-purple-400",
    amber: "bg-amber-500/20 text-amber-400",
    cyan: "bg-cyan-500/20 text-cyan-400",
    pink: "bg-pink-500/20 text-pink-400"
  };

  return (
    <Card className="bg-slate-900/60 border-slate-700">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-slate-400">{label}</p>
            <p className="text-xl font-bold text-white">{value}</p>
            {subValue && <p className="text-xs text-slate-500">{subValue}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Manuscript Detail Drawer
const ManuscriptDrawer = ({ manuscript, isOpen, onClose }) => {
  if (!manuscript) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-lg text-white">{manuscript.title}</DialogTitle>
          <DialogDescription className="text-slate-400">
            by {manuscript.author_name}
          </DialogDescription>
        </DialogHeader>

        <EngagementDisclaimer />

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-800/50 rounded-lg p-3">
              <p className="text-xs text-slate-500">RC Consumed</p>
              <p className="text-lg font-bold text-white">{formatRC(manuscript.total_rc_consumed)}</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <p className="text-xs text-slate-500">Playback Time</p>
              <p className="text-lg font-bold text-white">{formatSeconds(manuscript.total_playback_seconds)}</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <p className="text-xs text-slate-500">Unique Readers</p>
              <p className="text-lg font-bold text-white">{formatNumber(manuscript.total_readers)}</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <p className="text-xs text-slate-500">Completion Rate</p>
              <p className="text-lg font-bold text-white">{manuscript.completion_rate}%</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-slate-500">Upload Date</p>
              <p className="text-slate-300">{formatDate(manuscript.upload_date)}</p>
            </div>
            <div>
              <p className="text-slate-500">Age</p>
              <p className="text-slate-300">{manuscript.age_days} days</p>
            </div>
            <div>
              <p className="text-slate-500">Status</p>
              <Badge className={getStatusColor(manuscript.status)}>{manuscript.status}</Badge>
            </div>
            <div>
              <p className="text-slate-500">Last Activity</p>
              <p className="text-slate-300">{formatDate(manuscript.last_activity_at)}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AdminPublishersPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // View state
  const { notifyError } = useNotification();
  const [viewMode, setViewMode] = useState('list'); // 'list', 'detail', 'managed-authors', 'manuscripts'
  const [selectedPublisher, setSelectedPublisher] = useState(null);
  const [selectedManagedAuthor, setSelectedManagedAuthor] = useState(null);
  
  // Data state
  const [loading, setLoading] = useState(true);
  const [publishers, setPublishers] = useState([]);
  const [totalPublishers, setTotalPublishers] = useState(0);
  const [publisherDetail, setPublisherDetail] = useState(null);
  const [managedAuthors, setManagedAuthors] = useState([]);
  const [manuscripts, setManuscripts] = useState([]);
  const [engagement, setEngagement] = useState(null);
  
  // Drawer state
  const [selectedManuscript, setSelectedManuscript] = useState(null);
  const [showManuscriptDrawer, setShowManuscriptDrawer] = useState(false);
  
  // Managed Author manuscripts modal state
  const [maModalOpen, setMaModalOpen] = useState(false);
  const [maModalAuthor, setMaModalAuthor] = useState(null);
  const [maModalManuscripts, setMaModalManuscripts] = useState([]);
  const [maModalLoading, setMaModalLoading] = useState(false);

  // Delete-publisher modal state (double-confirmation)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Delete-managed-author modal state (double-confirmation)
  const [deleteMaTarget, setDeleteMaTarget] = useState(null);
  const [deleteMaConfirmText, setDeleteMaConfirmText] = useState('');
  const [deletingMa, setDeletingMa] = useState(false);
  const [deleteMaError, setDeleteMaError] = useState('');

  
  // Filters from URL
  const page = parseInt(searchParams.get('page') || '1');
  const pageSize = parseInt(searchParams.get('pageSize') || '20');
  const search = searchParams.get('search') || '';
  const dateRange = searchParams.get('dateRange') || '30';
  const publisherId = searchParams.get('publisher') || '';
  const managedAuthorId = searchParams.get('managedAuthor') || '';

  // Calculate date range
  const getDateParams = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - parseInt(dateRange));
    return {
      date_from: startDate.toISOString(),
      date_to: endDate.toISOString()
    };
  };

  // Update URL params
  const updateParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  // Fetch publishers list
  const fetchPublishers = useCallback(async () => {
    try {
      setLoading(true);
      const dates = getDateParams();
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        date_from: dates.date_from,
        date_to: dates.date_to,
        ...(search && { search })
      });

      const response = await fetch(`${API_URL}/api/admin/publishers?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch publishers');
      
      const data = await response.json();
      setPublishers(data.publishers || []);
      setTotalPublishers(data.total || 0);
    } catch (error) {
      console.error('Error fetching publishers:', error);
      notifyError('Failed to load publishers');
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, search, dateRange]);

  // Fetch publisher detail
  const fetchPublisherDetail = useCallback(async (pubId) => {
    try {
      setLoading(true);
      const dates = getDateParams();
      const params = new URLSearchParams({
        date_from: dates.date_from,
        date_to: dates.date_to
      });

      const response = await fetch(`${API_URL}/api/admin/publishers/${pubId}?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch publisher');
      
      const data = await response.json();
      setPublisherDetail(data);
      setSelectedPublisher(pubId);
    } catch (error) {
      console.error('Error fetching publisher:', error);
      notifyError('Failed to load publisher details');
    } finally {
      setLoading(false);
    }
  }, [token, dateRange]);

  // Fetch managed authors
  const fetchManagedAuthors = useCallback(async (pubId) => {
    try {
      setLoading(true);
      const dates = getDateParams();
      const params = new URLSearchParams({
        date_from: dates.date_from,
        date_to: dates.date_to
      });

      const response = await fetch(`${API_URL}/api/admin/publishers/${pubId}/managed-authors?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch managed authors');
      
      const data = await response.json();
      setManagedAuthors(data.managed_authors || []);
    } catch (error) {
      console.error('Error fetching managed authors:', error);
      notifyError('Failed to load managed authors');
    } finally {
      setLoading(false);
    }
  }, [token, dateRange]);

  // Fetch manuscripts
  const fetchManuscripts = useCallback(async (pubId, maId = null) => {
    try {
      setLoading(true);
      const dates = getDateParams();
      const params = new URLSearchParams({
        date_from: dates.date_from,
        date_to: dates.date_to,
        ...(maId && { managed_author_id: maId })
      });

      const response = await fetch(`${API_URL}/api/admin/publishers/${pubId}/manuscripts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch manuscripts');
      
      const data = await response.json();
      setManuscripts(data.manuscripts || []);
    } catch (error) {
      console.error('Error fetching manuscripts:', error);
      notifyError('Failed to load manuscripts');
    } finally {
      setLoading(false);
    }
  }, [token, dateRange]);

  // Fetch engagement data
  const fetchEngagement = useCallback(async (pubId) => {
    try {
      const dates = getDateParams();
      const params = new URLSearchParams({
        date_from: dates.date_from,
        date_to: dates.date_to
      });

      const response = await fetch(`${API_URL}/api/admin/publishers/${pubId}/engagement?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch engagement');
      
      const data = await response.json();
      setEngagement(data);
    } catch (error) {
      console.error('Error fetching engagement:', error);
    }
  }, [token, dateRange]);

  // Open the delete-publisher confirmation modal.
  const openDeleteModal = (e, pub) => {
    e.stopPropagation();
    setDeleteTarget(pub);
    setDeleteConfirmText('');
    setDeleteError('');
  };

  // Close the delete-publisher confirmation modal.
  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteConfirmText('');
    setDeleteError('');
  };

  // Submit the destructive delete. Requires confirm=true and the operator
  // to have typed the literal word DELETE into the input.
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmText.trim() !== 'DELETE') {
      setDeleteError('Type DELETE (all caps) to confirm.');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      const response = await fetch(
        `${API_URL}/api/admin/publishers/${deleteTarget.publisher_id}?confirm=true`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || `Delete failed (${response.status})`);
      }
      // Optimistically remove from list.
      setPublishers((prev) => prev.filter((p) => p.publisher_id !== deleteTarget.publisher_id));
      setTotalPublishers((prev) => Math.max(0, prev - 1));
      setDeleteTarget(null);
      setDeleteConfirmText('');
    } catch (err) {
      setDeleteError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // Open the delete-managed-author confirmation modal.
  const openDeleteMaModal = (e, ma) => {
    e.stopPropagation();
    setDeleteMaTarget(ma);
    setDeleteMaConfirmText('');
    setDeleteMaError('');
  };

  const closeDeleteMaModal = () => {
    if (deletingMa) return;
    setDeleteMaTarget(null);
    setDeleteMaConfirmText('');
    setDeleteMaError('');
  };

  // Submit the destructive delete for a Managed Author.
  const handleConfirmDeleteMa = async () => {
    if (!deleteMaTarget || !selectedPublisher) return;
    if (deleteMaConfirmText.trim() !== 'DELETE') {
      setDeleteMaError('Type DELETE (all caps) to confirm.');
      return;
    }
    setDeletingMa(true);
    setDeleteMaError('');
    try {
      const response = await fetch(
        `${API_URL}/api/admin/publishers/${selectedPublisher}/managed-authors/${deleteMaTarget.managed_author_id}?confirm=true`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || `Delete failed (${response.status})`);
      }
      setManagedAuthors((prev) =>
        prev.filter((m) => m.managed_author_id !== deleteMaTarget.managed_author_id)
      );
      setDeleteMaTarget(null);
      setDeleteMaConfirmText('');
    } catch (err) {
      setDeleteMaError(err.message || 'Delete failed');
    } finally {
      setDeletingMa(false);
    }
  };


  // Handle view publisher
  const handleViewPublisher = async (pubId) => {
    updateParams({ publisher: pubId });
    setViewMode('detail');
    await fetchPublisherDetail(pubId);
    await fetchManagedAuthors(pubId);
    await fetchManuscripts(pubId);
    await fetchEngagement(pubId);
  };

  // Handle drill-down to managed author
  const handleViewManagedAuthor = async (ma) => {
    setSelectedManagedAuthor(ma);
    updateParams({ managedAuthor: ma.managed_author_id });
    setViewMode('manuscripts');
    await fetchManuscripts(selectedPublisher, ma.managed_author_id);
  };

  // Handle managed author name click — show manuscripts modal
  const handleMaNameClick = async (e, ma) => {
    e.stopPropagation();
    setMaModalAuthor(ma);
    setMaModalOpen(true);
    setMaModalLoading(true);
    try {
      const dates = getDateParams();
      const params = new URLSearchParams({
        date_from: dates.date_from,
        date_to: dates.date_to,
        managed_author_id: ma.managed_author_id
      });
      const response = await fetch(`${API_URL}/api/admin/publishers/${selectedPublisher}/manuscripts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMaModalManuscripts(data.manuscripts || []);
      }
    } catch (error) {
      console.error('Error fetching managed author manuscripts:', error);
    } finally {
      setMaModalLoading(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (viewMode === 'manuscripts' && selectedManagedAuthor) {
      setSelectedManagedAuthor(null);
      updateParams({ managedAuthor: '' });
      setViewMode('detail');
      fetchManuscripts(selectedPublisher);
    } else {
      setViewMode('list');
      setSelectedPublisher(null);
      setPublisherDetail(null);
      updateParams({ publisher: '', managedAuthor: '' });
    }
  };

  // Initial load
  useEffect(() => {
    if (publisherId) {
      handleViewPublisher(publisherId);
    } else {
      fetchPublishers();
    }
  }, []);

  // Reload on date range change
  useEffect(() => {
    if (viewMode === 'list') {
      fetchPublishers();
    } else if (selectedPublisher) {
      fetchPublisherDetail(selectedPublisher);
      fetchManagedAuthors(selectedPublisher);
      fetchManuscripts(selectedPublisher, selectedManagedAuthor?.managed_author_id);
      fetchEngagement(selectedPublisher);
    }
  }, [dateRange]);

  // Refresh handler
  const handleRefresh = () => {
    if (viewMode === 'list') {
      fetchPublishers();
    } else if (selectedPublisher) {
      fetchPublisherDetail(selectedPublisher);
      fetchManagedAuthors(selectedPublisher);
      fetchManuscripts(selectedPublisher, selectedManagedAuthor?.managed_author_id);
      fetchEngagement(selectedPublisher);
    }
    console.log('[SUCCESS] ' + 'Data refreshed');
  };

  // Build breadcrumb items
  const getBreadcrumbItems = () => {
    const items = [{ label: 'Publishers', onClick: viewMode !== 'list' ? handleBack : null }];
    
    if (publisherDetail && viewMode !== 'list') {
      items.push({
        label: publisherDetail.dba_name || publisherDetail.legal_business_name,
        onClick: selectedManagedAuthor ? () => {
          setSelectedManagedAuthor(null);
          updateParams({ managedAuthor: '' });
          setViewMode('detail');
        } : null
      });
    }
    
    if (selectedManagedAuthor) {
      items.push({ label: selectedManagedAuthor.pen_name });
    }
    
    return items;
  };

  const totalPages = Math.ceil(totalPublishers / pageSize);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <AdminNav />
      <AdminTopMenuBar />
      
      <div className="pb-20 md:pb-0">
        <main className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-1">
                  Publisher Management Console
                </h1>
                <p className="text-slate-400">
                  Governance analytics for publishers, managed authors, and content performance.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={dateRange} onValueChange={(v) => updateParams({ dateRange: v })}>
                  <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700">
                    <Calendar className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                    <SelectItem value="365">Last year</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleRefresh} variant="outline" size="sm">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {/* Breadcrumb */}
          {viewMode !== 'list' && <Breadcrumb items={getBreadcrumbItems()} />}

          {/* Context Banner */}
          <EngagementDisclaimer />

          {/* Content based on view mode */}
          {viewMode === 'list' ? (
            /* Publisher List View */
            <>
              {/* Filters */}
              <Card className="bg-slate-900/60 border-slate-700 mb-6">
                <CardContent className="p-4">
                  <div className="flex gap-4 items-center">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Search publishers..."
                          value={search}
                          onChange={(e) => updateParams({ search: e.target.value, page: '1' })}
                          className="pl-10 bg-slate-800 border-slate-700"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Publishers Table */}
              <Card className="bg-slate-900/60 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Publishers</CardTitle>
                  <CardDescription>Click on a publisher to drill down into their analytics</CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    </div>
                  ) : publishers.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      No publishers found.
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-400">Publisher</TableHead>
                          <TableHead className="text-slate-400 text-right">Authors</TableHead>
                          <TableHead className="text-slate-400 text-right">Books</TableHead>
                          <TableHead className="text-slate-400 text-right">Readers</TableHead>
                          <TableHead className="text-slate-400 text-right">RC Consumed</TableHead>
                          <TableHead className="text-slate-400 text-right">Playback Time</TableHead>
                          <TableHead className="text-slate-400">Last Activity</TableHead>
                          <TableHead className="text-slate-400"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {publishers.map((pub) => (
                          <TableRow 
                            key={pub.publisher_id} 
                            className="border-slate-700 hover:bg-slate-800/50 cursor-pointer"
                            onClick={() => handleViewPublisher(pub.publisher_id)}
                          >
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                                  <Building2 className="w-5 h-5 text-slate-400" />
                                </div>
                                <div>
                                  <p className="font-medium text-white">{pub.publisher_name}</p>
                                  <Badge className={`text-xs ${getStatusColor(pub.status)}`}>{pub.status}</Badge>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-slate-300">{pub.total_managed_authors}</TableCell>
                            <TableCell className="text-right text-slate-300">{pub.total_manuscripts}</TableCell>
                            <TableCell className="text-right text-slate-300">{pub.total_readers}</TableCell>
                            <TableCell className="text-right text-slate-300">{formatRC(pub.total_rc_consumed)}</TableCell>
                            <TableCell className="text-right text-slate-300">{formatSeconds(pub.total_playback_seconds)}</TableCell>
                            <TableCell className="text-slate-400">{formatDate(pub.last_activity_at)}</TableCell>
                            <TableCell>
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="sm" aria-label="View publisher">
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                                  onClick={(e) => openDeleteModal(e, pub)}
                                  data-testid={`delete-publisher-${pub.publisher_id}`}
                                  title="Delete publisher record"
                                  aria-label="Delete publisher"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            /* Publisher Detail View */
            <>
              {/* Back Button */}
              <Button variant="ghost" onClick={handleBack} className="mb-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>

              {publisherDetail && (
                <>
                  {/* Publisher Header */}
                  <Card className="bg-slate-900/60 border-slate-700 mb-6">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          {publisherDetail.logo_url ? (
                            <img 
                              src={`${API_URL}${publisherDetail.logo_url}`}
                              alt={publisherDetail.dba_name}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-slate-700 flex items-center justify-center">
                              <Building2 className="w-8 h-8 text-slate-400" />
                            </div>
                          )}
                          <div>
                            <h2 className="text-2xl font-bold text-white">
                              {publisherDetail.dba_name || publisherDetail.legal_business_name}
                            </h2>
                            <p className="text-slate-400">{publisherDetail.legal_business_name}</p>
                            <Badge className={`mt-1 ${getStatusColor(publisherDetail.status)}`}>
                              {publisherDetail.status}
                            </Badge>
                            <div className="mt-2 flex items-center gap-2">
                              <span className="text-xs text-slate-500">Publisher ID:</span>
                              <code
                                data-testid="publisher-id-copyable"
                                className="text-xs text-amber-400 bg-slate-800 px-2 py-0.5 rounded cursor-pointer hover:bg-slate-700 transition-colors font-mono select-all"
                                title="Click to copy"
                                onClick={(e) => {
                                  navigator.clipboard.writeText(publisherDetail.publisher_id);
                                  e.target.classList.add('ring-1', 'ring-amber-400');
                                  setTimeout(() => e.target.classList.remove('ring-1', 'ring-amber-400'), 800);
                                }}
                              >
                                {publisherDetail.publisher_id}
                              </code>
                            </div>
                          </div>
                        </div>
                        <div className="text-right text-sm">
                          <p className="text-slate-500">Contact</p>
                          <p className="text-slate-300">{publisherDetail.primary_contact_name}</p>
                          <p className="text-slate-400">{publisherDetail.primary_contact_email}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Summary Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                    <MetricCard 
                      icon={Users} 
                      label="Managed Authors" 
                      value={publisherDetail.total_managed_authors} 
                      color="purple"
                    />
                    <MetricCard 
                      icon={BookOpen} 
                      label="Manuscripts" 
                      value={publisherDetail.total_manuscripts} 
                      color="blue"
                    />
                    <MetricCard 
                      icon={Users} 
                      label="Readers" 
                      value={publisherDetail.total_readers} 
                      color="green"
                    />
                    <MetricCard 
                      icon={CreditCard} 
                      label="RC Consumed" 
                      value={formatRC(publisherDetail.total_rc_consumed)} 
                      color="amber"
                    />
                    <MetricCard 
                      icon={Clock} 
                      label="Playback Time" 
                      value={formatSeconds(publisherDetail.total_playback_seconds)} 
                      color="cyan"
                    />
                    <MetricCard 
                      icon={MessageSquare} 
                      label="Ask Audia" 
                      value={publisherDetail.total_ask_audia_sessions} 
                      color="pink"
                    />
                  </div>

                  {/* Tabs */}
                  <Tabs defaultValue={selectedManagedAuthor ? "manuscripts" : "managed-authors"} className="space-y-4">
                    <TabsList className="bg-slate-800">
                      <TabsTrigger value="managed-authors">Managed Authors</TabsTrigger>
                      <TabsTrigger value="manuscripts">Manuscripts</TabsTrigger>
                      <TabsTrigger value="top-performers">Top Performers</TabsTrigger>
                    </TabsList>

                    {/* Managed Authors Tab */}
                    <TabsContent value="managed-authors">
                      <Card className="bg-slate-900/60 border-slate-700">
                        <CardHeader>
                          <CardTitle className="text-white">Managed Authors</CardTitle>
                          <CardDescription>Authors under this publisher&apos;s management</CardDescription>
                        </CardHeader>
                        <CardContent>
                          {loading ? (
                            <div className="flex items-center justify-center py-12">
                              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                            </div>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow className="border-slate-700">
                                  <TableHead className="text-slate-400">Author</TableHead>
                                  <TableHead className="text-slate-400 text-right">Books</TableHead>
                                  <TableHead className="text-slate-400 text-right">Readers</TableHead>
                                  <TableHead className="text-slate-400 text-right">RC Consumed</TableHead>
                                  <TableHead className="text-slate-400 text-right">Playback Time</TableHead>
                                  <TableHead className="text-slate-400">Last Activity</TableHead>
                                  <TableHead className="text-slate-400"></TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {managedAuthors.map((ma) => (
                                  <TableRow 
                                    key={ma.managed_author_id} 
                                    className="border-slate-700 hover:bg-slate-800/50 cursor-pointer"
                                    onClick={() => handleViewManagedAuthor(ma)}
                                  >
                                    <TableCell>
                                      <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                                          <Users className="w-4 h-4 text-slate-400" />
                                        </div>
                                        <button
                                          onClick={(e) => handleMaNameClick(e, ma)}
                                          className="font-medium text-blue-400 hover:text-blue-300 hover:underline text-left"
                                          data-testid={`ma-name-${ma.managed_author_id}`}
                                        >
                                          {ma.pen_name}
                                        </button>
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-right text-slate-300">{ma.total_manuscripts}</TableCell>
                                    <TableCell className="text-right text-slate-300">{ma.total_readers}</TableCell>
                                    <TableCell className="text-right text-slate-300">{formatRC(ma.total_rc_consumed)}</TableCell>
                                    <TableCell className="text-right text-slate-300">{formatSeconds(ma.total_playback_seconds)}</TableCell>
                                    <TableCell className="text-slate-400">{formatDate(ma.last_activity_at)}</TableCell>
                                    <TableCell>
                                      <div className="flex items-center justify-end gap-1">
                                        <Button variant="ghost" size="sm" aria-label="View managed author">
                                          <ChevronRight className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                                          onClick={(e) => openDeleteMaModal(e, ma)}
                                          data-testid={`delete-managed-author-${ma.managed_author_id}`}
                                          title="Delete managed author"
                                          aria-label="Delete managed author"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* Manuscripts Tab */}
                    <TabsContent value="manuscripts">
                      <Card className="bg-slate-900/60 border-slate-700">
                        <CardHeader>
                          <CardTitle className="text-white">
                            {selectedManagedAuthor 
                              ? `Manuscripts by ${selectedManagedAuthor.pen_name}`
                              : 'All Manuscripts'
                            }
                          </CardTitle>
                          <CardDescription>
                            {selectedManagedAuthor 
                              ? `Showing books for this managed author`
                              : 'All books under this publisher'
                            }
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {loading ? (
                            <div className="flex items-center justify-center py-12">
                              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                            </div>
                          ) : manuscripts.length === 0 ? (
                            <div className="text-center py-12 text-slate-400">
                              No manuscripts found.
                            </div>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow className="border-slate-700">
                                  <TableHead className="text-slate-400">Title</TableHead>
                                  <TableHead className="text-slate-400">Author</TableHead>
                                  <TableHead className="text-slate-400 text-right">Readers</TableHead>
                                  <TableHead className="text-slate-400 text-right">RC Consumed</TableHead>
                                  <TableHead className="text-slate-400 text-right">Playback</TableHead>
                                  <TableHead className="text-slate-400 text-right">Completion</TableHead>
                                  <TableHead className="text-slate-400">Age</TableHead>
                                  <TableHead className="text-slate-400"></TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {manuscripts.map((ms) => (
                                  <TableRow 
                                    key={ms.manuscript_id} 
                                    className="border-slate-700 hover:bg-slate-800/50"
                                  >
                                    <TableCell className="font-medium text-white">{ms.title}</TableCell>
                                    <TableCell className="text-slate-300">{ms.author_name}</TableCell>
                                    <TableCell className="text-right text-slate-300">{ms.total_readers}</TableCell>
                                    <TableCell className="text-right text-slate-300">{formatRC(ms.total_rc_consumed)}</TableCell>
                                    <TableCell className="text-right text-slate-300">{formatSeconds(ms.total_playback_seconds)}</TableCell>
                                    <TableCell className="text-right text-slate-300">{ms.completion_rate}%</TableCell>
                                    <TableCell className="text-slate-400">{ms.age_days}d</TableCell>
                                    <TableCell>
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedManuscript(ms);
                                          setShowManuscriptDrawer(true);
                                        }}
                                      >
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* Top Performers Tab */}
                    <TabsContent value="top-performers">
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Top Titles */}
                        <Card className="bg-slate-900/60 border-slate-700">
                          <CardHeader>
                            <CardTitle className="text-white text-lg flex items-center gap-2">
                              <BarChart3 className="w-5 h-5 text-blue-400" />
                              Top 5 Titles by RC Consumed
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {publisherDetail.top_titles_by_rc?.length > 0 ? (
                              <div className="space-y-3">
                                {publisherDetail.top_titles_by_rc.map((title, idx) => (
                                  <div key={title.manuscript_id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                      <span className="text-lg font-bold text-slate-500">#{idx + 1}</span>
                                      <div>
                                        <p className="font-medium text-white text-sm">{title.title}</p>
                                        <p className="text-xs text-slate-400">{title.author}</p>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-semibold text-white">{formatRC(title.rc_consumed)}</p>
                                      <p className="text-xs text-slate-400">{formatSeconds(title.playback_seconds)}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-slate-400 text-center py-4">No data available</p>
                            )}
                          </CardContent>
                        </Card>

                        {/* Top Managed Authors */}
                        <Card className="bg-slate-900/60 border-slate-700">
                          <CardHeader>
                            <CardTitle className="text-white text-lg flex items-center gap-2">
                              <TrendingUp className="w-5 h-5 text-purple-400" />
                              Top 5 Managed Authors
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {engagement?.top_managed_authors_by_rc?.length > 0 ? (
                              <div className="space-y-3">
                                {engagement.top_managed_authors_by_rc.map((author, idx) => (
                                  <div key={author.managed_author_id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                      <span className="text-lg font-bold text-slate-500">#{idx + 1}</span>
                                      <p className="font-medium text-white text-sm">{author.pen_name}</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-semibold text-white">{formatRC(author.rc_consumed)}</p>
                                      <p className="text-xs text-slate-400">{formatSeconds(author.playback_seconds)}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-slate-400 text-center py-4">No data available</p>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </>
          )}
        </main>
      </div>

      {/* Manuscript Drawer */}
      <ManuscriptDrawer
        manuscript={selectedManuscript}
        isOpen={showManuscriptDrawer}
        onClose={() => setShowManuscriptDrawer(false)}
      />

      {/* Managed Author Manuscripts Modal */}
      <Dialog open={maModalOpen} onOpenChange={setMaModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-lg">
              {maModalAuthor?.pen_name} — Manuscripts
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Books associated with this managed author
            </DialogDescription>
          </DialogHeader>
          {maModalLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : maModalManuscripts.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              No manuscripts found for this author.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Book Title</TableHead>
                  <TableHead className="text-slate-400 text-right">Readers</TableHead>
                  <TableHead className="text-slate-400 text-right">RC Consumed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {maModalManuscripts.map((ms) => (
                  <TableRow key={ms.manuscript_id} className="border-slate-700">
                    <TableCell className="font-medium text-white">{ms.title}</TableCell>
                    <TableCell className="text-right text-slate-300">{ms.total_readers}</TableCell>
                    <TableCell className="text-right text-slate-300">{formatRC(ms.total_rc_consumed)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Publisher Confirmation Modal (double-confirmation) */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) closeDeleteModal(); }}>
        <DialogContent className="bg-slate-900 border-red-900/60 max-w-md" data-testid="delete-publisher-modal">
          <DialogHeader>
            <DialogTitle className="text-red-300 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Publisher Record
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This permanently removes the publisher row from the database. The user
              account itself is <span className="text-slate-200">not</span> affected — use
              the Users page if you also want to archive the account.
            </DialogDescription>
          </DialogHeader>

          {deleteTarget && (
            <div className="space-y-3">
              <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Publisher</p>
                <p className="text-white font-semibold">{deleteTarget.publisher_name}</p>
                <p className="font-mono text-xs text-slate-500 mt-1 break-all">{deleteTarget.publisher_id}</p>
              </div>

              <div className="rounded-lg border border-amber-700/50 bg-amber-900/15 p-3 text-xs text-amber-200">
                Any orphan managed-author shells under this publisher will also be removed.
                The delete is refused if real manuscript content is still linked.
              </div>

              <div>
                <label htmlFor="delete-confirm-input" className="text-xs text-slate-400 block mb-1.5">
                  Type <span className="font-mono text-red-300">DELETE</span> to confirm:
                </label>
                <Input
                  id="delete-confirm-input"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  autoComplete="off"
                  data-testid="delete-publisher-confirm-input"
                  className="bg-slate-950 border-slate-700 font-mono text-white"
                  disabled={deleting}
                />
              </div>

              {deleteError && (
                <p className="text-sm text-red-300" data-testid="delete-publisher-error">
                  {deleteError}
                </p>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
                  onClick={closeDeleteModal}
                  disabled={deleting}
                  data-testid="delete-publisher-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-500 text-white"
                  onClick={handleConfirmDelete}
                  disabled={deleting || deleteConfirmText.trim() !== 'DELETE'}
                  data-testid="delete-publisher-confirm-btn"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting…
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Permanently
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Managed Author Confirmation Modal (double-confirmation) */}
      <Dialog open={!!deleteMaTarget} onOpenChange={(open) => { if (!open) closeDeleteMaModal(); }}>
        <DialogContent className="bg-slate-900 border-red-900/60 max-w-md" data-testid="delete-managed-author-modal">
          <DialogHeader>
            <DialogTitle className="text-red-300 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Managed Author
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This permanently removes the managed-author row under this publisher.
              The delete is refused if any manuscript content is still linked to this author.
            </DialogDescription>
          </DialogHeader>

          {deleteMaTarget && (
            <div className="space-y-3">
              <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Managed Author</p>
                <p className="text-white font-semibold">{deleteMaTarget.pen_name}</p>
                <p className="font-mono text-xs text-slate-500 mt-1 break-all">{deleteMaTarget.managed_author_id}</p>
                {typeof deleteMaTarget.total_manuscripts === 'number' && (
                  <p className="text-xs text-slate-400 mt-2">
                    Linked manuscripts: <span className="text-white font-semibold">{deleteMaTarget.total_manuscripts}</span>
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="delete-ma-confirm-input" className="text-xs text-slate-400 block mb-1.5">
                  Type <span className="font-mono text-red-300">DELETE</span> to confirm:
                </label>
                <Input
                  id="delete-ma-confirm-input"
                  value={deleteMaConfirmText}
                  onChange={(e) => setDeleteMaConfirmText(e.target.value)}
                  placeholder="DELETE"
                  autoComplete="off"
                  data-testid="delete-managed-author-confirm-input"
                  className="bg-slate-950 border-slate-700 font-mono text-white"
                  disabled={deletingMa}
                />
              </div>

              {deleteMaError && (
                <p className="text-sm text-red-300" data-testid="delete-managed-author-error">
                  {deleteMaError}
                </p>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
                  onClick={closeDeleteMaModal}
                  disabled={deletingMa}
                  data-testid="delete-managed-author-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-500 text-white"
                  onClick={handleConfirmDeleteMa}
                  disabled={deletingMa || deleteMaConfirmText.trim() !== 'DELETE'}
                  data-testid="delete-managed-author-confirm-btn"
                >
                  {deletingMa ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting…
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Permanently
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AdminMobileBottomNav />
      <UniversalFooter />
    </div>
  );
};

export default AdminPublishersPage;
